import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  REFERENCE_LAYER_TYPE_LABELS,
  formatReferenceLayerApiError,
  geometryKindLabel,
  previewReferenceLayerFile,
  updateReferenceLayer,
  uploadReferenceLayerFile,
  type ReferenceFieldStat,
  type ReferenceGeometryKind,
  type ReferenceLayerDto,
  type ReferenceLayerPreviewDto,
  type ReferenceLayerStyle,
  type ReferenceLayerType,
} from '../../services/referenceLayerApi';
import { ReferenceStyleControls } from '../map/ReferenceLayersSection';

type Props = {
  open: boolean;
  onClose: () => void;
  /** New upload (file) or edit of an existing layer (layer). */
  file?: File | null;
  layer?: ReferenceLayerDto | null;
  onSaved: (layer: ReferenceLayerDto) => void;
};

const LAYER_TYPES = Object.keys(REFERENCE_LAYER_TYPE_LABELS) as ReferenceLayerType[];

/**
 * Admin import / edit dialog: preview the parsed file (geometry kind, fields,
 * warnings, sample rows), set name / type / label field, and tune the default
 * style that every user starts from.
 */
const ReferenceLayerImportDialog: React.FC<Props> = ({ open, onClose, file, layer, onSaved }) => {
  const editing = Boolean(layer && !file);
  const [preview, setPreview] = useState<ReferenceLayerPreviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [layerType, setLayerType] = useState<ReferenceLayerType>('custom');
  const [labelField, setLabelField] = useState<string>('');
  const [source, setSource] = useState('');
  const [style, setStyle] = useState<ReferenceLayerStyle | null>(null);
  const [latField, setLatField] = useState('');
  const [lonField, setLonField] = useState('');

  const geometryKind: ReferenceGeometryKind | null = editing ? layer!.geometry_kind : preview?.geometry_kind ?? null;
  const fields = useMemo<Record<string, ReferenceFieldStat>>(
    () => (editing ? layer?.fields ?? {} : preview?.fields ?? {}),
    [editing, layer, preview]
  );
  const fieldNames = useMemo(() => Object.keys(fields).sort((a, b) => a.localeCompare(b)), [fields]);
  const isTabular = !editing && Boolean(preview && ['csv', 'rows'].includes(preview.source_format));

  // Seed state from the layer (edit) or run the preview (new file).
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing && layer) {
      setPreview(null);
      setName(layer.name);
      setDescription(layer.description || '');
      setLayerType(layer.layer_type);
      setLabelField(layer.label_field || '');
      setSource(layer.source || '');
      setStyle(layer.style);
      return;
    }
    if (!file) return;
    setPreview(null);
    setStyle(null);
    setName('');
    setDescription('');
    setLayerType('custom');
    setLabelField('');
    setSource('');
    setLatField('');
    setLonField('');
    setLoading(true);
    void (async () => {
      try {
        const p = await previewReferenceLayerFile(file, {});
        setPreview(p);
        setName(p.suggested_name);
        setLabelField(p.label_field || '');
        setStyle(p.style);
        // Guess a layer type from the file name.
        const lower = file.name.toLowerCase();
        if (/boundar|gang|territor|border/.test(lower)) setLayerType('boundaries');
        else if (/beat|district|precinct|ward|zone|sector|tract/.test(lower)) setLayerType('districts');
        else if (/road|route|corridor|rail|transit|line/.test(lower)) setLayerType('routes');
        else if (/poi|place|site|school|hospital|station/.test(lower)) setLayerType('poi');
        else if (/infra|utility|pipeline|tower|bridge|light/.test(lower)) setLayerType('infrastructure');
      } catch (e) {
        setError(formatReferenceLayerApiError(e, 'Could not read the file'));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, editing, layer, file]);

  // Re-run the preview when the admin maps lat/lon columns for a CSV.
  const rerunPreview = useCallback(
    async (lat: string, lon: string) => {
      if (!file) return;
      setLoading(true);
      setError(null);
      try {
        const p = await previewReferenceLayerFile(file, { mapping: { latitude: lat || null, longitude: lon || null } });
        setPreview(p);
        if (!labelField && p.label_field) setLabelField(p.label_field);
        if (!style) setStyle(p.style);
      } catch (e) {
        setError(formatReferenceLayerApiError(e, 'Could not read the file'));
      } finally {
        setLoading(false);
      }
    },
    [file, labelField, style]
  );

  // When the type changes before the admin touched colours, pick the type's default palette.
  const handleTypeChange = (t: ReferenceLayerType) => {
    setLayerType(t);
  };

  const canSave = Boolean(name.trim()) && Boolean(style) && (editing || Boolean(preview)) && !loading && !saving;

  const handleSave = async () => {
    if (!style) return;
    setSaving(true);
    setError(null);
    try {
      let saved: ReferenceLayerDto;
      if (editing && layer) {
        saved = await updateReferenceLayer(layer.id, {
          name: name.trim(),
          description: description.trim() || null,
          layer_type: layerType,
          label_field: labelField || null,
          source: source.trim() || null,
          style,
        });
      } else if (file) {
        saved = await uploadReferenceLayerFile(file, {
          name: name.trim(),
          description: description.trim() || null,
          layer_type: layerType,
          label_field: labelField || null,
          source: source.trim() || null,
          style,
          mapping: isTabular ? { latitude: latField || null, longitude: lonField || null } : null,
        });
      } else {
        return;
      }
      onSaved(saved);
      onClose();
    } catch (e) {
      setError(formatReferenceLayerApiError(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const sample = useMemo(() => preview?.sample ?? [], [preview]);
  const sampleCols = useMemo(() => {
    const cols: string[] = [];
    for (const f of sample) {
      for (const k of Object.keys(f.properties || {})) {
        if (!cols.includes(k)) cols.push(k);
        if (cols.length >= 6) break;
      }
      if (cols.length >= 6) break;
    }
    return cols;
  }, [sample]);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        {editing ? 'Edit reference layer' : 'Import reference layer'}
        {(file || layer) && (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            {file ? file.name : layer?.source_format ? `${layer.name} · ${layer.source_format}` : layer?.name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Reading file…
            </Typography>
          </Stack>
        )}

        {/* Summary chips */}
        {(preview || editing) && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {geometryKind && <Chip size="small" color="primary" label={geometryKindLabel(geometryKind)} />}
            <Chip size="small" variant="outlined" label={`${(editing ? layer!.feature_count : preview!.feature_count).toLocaleString()} features`} />
            {preview && preview.skipped > 0 && <Chip size="small" variant="outlined" color="warning" label={`${preview.skipped} skipped`} />}
            {preview && <Chip size="small" variant="outlined" label={preview.source_format_label} />}
            <Chip size="small" variant="outlined" label={`${fieldNames.length} fields`} />
          </Stack>
        )}
        {preview?.warnings.map((w) => (
          <Alert key={w} severity="warning" sx={{ mb: 1, py: 0 }}>
            {w}
          </Alert>
        ))}

        {/* CSV lat/lon mapping */}
        {isTabular && (
          <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Latitude column</InputLabel>
              <Select
                label="Latitude column"
                value={latField}
                onChange={(e) => {
                  setLatField(e.target.value as string);
                  void rerunPreview(e.target.value as string, lonField);
                }}
              >
                <MenuItem value="">
                  <em>Auto</em>
                </MenuItem>
                {fieldNames.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Longitude column</InputLabel>
              <Select
                label="Longitude column"
                value={lonField}
                onChange={(e) => {
                  setLonField(e.target.value as string);
                  void rerunPreview(latField, e.target.value as string);
                }}
              >
                <MenuItem value="">
                  <em>Auto</em>
                </MenuItem>
                {fieldNames.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}

        {/* Identity */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 1.5, mb: 1.5 }}>
          <TextField size="small" label="Layer name" value={name} onChange={(e) => setName(e.target.value)} required disabled={loading} />
          <FormControl size="small">
            <InputLabel>Layer type</InputLabel>
            <Select label="Layer type" value={layerType} onChange={(e) => handleTypeChange(e.target.value as ReferenceLayerType)}>
              {LAYER_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {REFERENCE_LAYER_TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
          <TextField size="small" label="Source (optional)" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. Chicago PD ClearMap" />
          <FormControl size="small">
            <InputLabel>Label field</InputLabel>
            <Select label="Label field" value={labelField} onChange={(e) => setLabelField(e.target.value as string)} disabled={fieldNames.length === 0}>
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {fieldNames.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                  <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.75 }}>
                    {fields[f].kind === 'number' ? 'number' : `${fields[f].distinct} distinct`}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Default style */}
        {style && geometryKind && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Default style
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Users start from this and can adjust it in their own map settings.
              </Typography>
            </Typography>
            <ReferenceStyleControls geometryKind={geometryKind} fields={fields} labelField={labelField || null} style={style} onChange={(patch) => setStyle((s) => (s ? { ...s, ...patch } : s))} />
          </>
        )}

        {/* Sample */}
        {sample.length > 0 && sampleCols.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Sample
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.7rem' }}>Geometry</TableCell>
                    {sampleCols.map((c) => (
                      <TableCell key={c} sx={{ fontSize: '0.7rem' }}>
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sample.slice(0, 5).map((f, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{f.geometry.type}</TableCell>
                      {sampleCols.map((c) => (
                        <TableCell key={c} sx={{ fontSize: '0.7rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.properties?.[c] == null ? '' : String(f.properties[c])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Saving…' : editing ? 'Save' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReferenceLayerImportDialog;
